import PropTypes from "prop-types";
import React from "react";
import {Helmet} from "react-helmet";
import i18n from "../i18n";

const SEO = ({
  title = "Happy Solutions",
  titleKey,
  description,
  descriptionKey,
  url,
  ogImage,
  canonical,
  locale,
  jsonLd,
}) => {
  const lng = (i18n.language || 'hu').slice(0,2);
  const localeMap = { hu: 'hu_HU', en: 'en_US', de: 'de_DE' };
  const resolvedLocale = (locale && locale.length ? locale : localeMap[lng]) || 'hu_HU';
  const siteName = i18n.t('common.appName') || 'Happy Solutions';
  const resolvedTitle = titleKey ? i18n.t(titleKey) : title;
  const pageTitle = resolvedTitle ? `${resolvedTitle} - ${siteName}` : siteName;
  const defaultDesc = i18n.t('seo.defaultDesc');
  const resolvedDesc = descriptionKey ? i18n.t(descriptionKey) : description;
  const pageDesc = resolvedDesc || defaultDesc;
  const lang = resolvedLocale.split("_")[0];

  // Build default JSON-LD for WebSite and Organization
  const origin = (typeof window !== 'undefined' && window.location && window.location.origin) || 'https://happygastro.hu';
  const websiteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: origin,
    inLanguage: lng,
    description: pageDesc,
  };
  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteName,
    url: origin,
    description: pageDesc,
  };

  const toArray = (data) => (Array.isArray(data) ? data : (data ? [data] : []));
  const providedLd = toArray(jsonLd).map(obj => ({
    '@context': 'https://schema.org',
    inLanguage: lng,
    ...obj,
    // do not overwrite provided @type or fields; ensure @context/inLanguage exist
  }));
  const allLd = [websiteLd, orgLd, ...providedLd];

  return (
    <Helmet htmlAttributes={{ lang }}>
      <meta charSet="utf-8" />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
      />
      <title>{pageTitle}</title>
      <meta name="robots" content="index, follow" />
      <meta name="description" content={pageDesc} />
      {(canonical || url) && <link rel="canonical" href={canonical || url} />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={resolvedLocale} />
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* JSON-LD */}
      {allLd.map((obj, idx) => (
        <script key={`ld-${idx}`} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  titleKey: PropTypes.string,
  description: PropTypes.string,
  descriptionKey: PropTypes.string,
  url: PropTypes.string,
  ogImage: PropTypes.string,
  canonical: PropTypes.string,
  locale: PropTypes.string,
  jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};

export default SEO;
